// ============================================================================
// StuecklistePrintSheet — Anlagen-Stückliste + Montageplan (Druckblatt)
// ----------------------------------------------------------------------------
// pvXpert-artiges Deliverable: konsolidiert die gewählten/empfohlenen
// Komponenten (Module, Wechselrichter, Speicher, String-Konfiguration) plus
// Montage-Eckdaten zu einem druckbaren A4-Blatt. Ergänzt das Kosten-Druckblatt
// (Material+Arbeit) um eine komponentenbezogene Bauteilliste (BOM).
//
// Sicherheitsinvarianten bleiben sichtbar: ENTWURF · kein Angebot · keine
// Freigabe · menschliche Prüfung erforderlich. Reines Frontend (Druck), kein
// Persist. Quelle der Empfehlung: /api/components/empfehlung (Engine).
// ============================================================================

// ─── Typen (minimale Spiegel der Empfehlungs-Engine-Ausgabe) ──────────────────

export interface StuecklisteWechselrichter {
  hersteller: string;
  modell: string;
  leistungKw: number;
  preisEur: number;
  mppTrackers: number;
}

export interface StuecklisteBatterie {
  hersteller: string;
  modell: string;
  preisEur: number;
}

export interface StuecklisteStringKonfig {
  mppTracker: number;
  stringLaenge: number;
  modulePro: number[];
}

export interface StuecklistePrintSheetProps {
  kwp: number;
  /** Gewähltes Modul (Katalog) oder null = Standard-Annahme. */
  modulName: string | null;
  modulWp: number;
  modulVoc: number;
  moduleCount: number;
  wechselrichter: StuecklisteWechselrichter;
  batterie: StuecklisteBatterie | null;
  batterieKwh: number;
  stringKonfiguration: StuecklisteStringKonfig;
  gesamtpreisNetto: number;
  gesamtpreisBrutto: number;
  projectName?: string;
  customerName?: string;
  onClose: () => void;
}

const eur = (n: number) =>
  n.toLocaleString("de-AT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

const PRINT_CSS = `
  @media print {
    body * { visibility: hidden; }
    #bom-print-overlay, #bom-print-overlay * { visibility: visible; }
    #bom-print-overlay {
      position: absolute; left: 0; top: 0; width: 100%;
      box-shadow: none !important; margin: 0 !important; padding: 24px !important;
    }
    .bom-noprint { display: none !important; }
  }
`;

interface BomRow {
  pos: number;
  menge: string;
  bezeichnung: string;
  detail: string;
}

export function StuecklistePrintSheet(props: StuecklistePrintSheetProps) {
  const {
    kwp, modulName, modulWp, modulVoc, moduleCount, wechselrichter, batterie,
    batterieKwh, stringKonfiguration, gesamtpreisNetto, gesamtpreisBrutto,
    projectName, customerName, onClose,
  } = props;

  // ── Bauteilliste (BOM) zusammenstellen ──────────────────────────────────────
  const rows: BomRow[] = [];
  let pos = 1;
  rows.push({
    pos: pos++,
    menge: `${moduleCount} Stk`,
    bezeichnung: modulName ?? `PV-Modul ${modulWp} Wp (Standard-Annahme)`,
    detail: `${modulWp} Wp · Voc ${modulVoc} V · ${(moduleCount * modulWp / 1000).toFixed(2)} kWp gesamt`,
  });
  rows.push({
    pos: pos++,
    menge: "1 Stk",
    bezeichnung: `${wechselrichter.hersteller} ${wechselrichter.modell}`,
    detail: `${wechselrichter.leistungKw} kW · ${wechselrichter.mppTrackers} MPP-Tracker · ${eur(wechselrichter.preisEur)} netto`,
  });
  if (batterie) {
    rows.push({
      pos: pos++,
      menge: "1 Stk",
      bezeichnung: `${batterie.hersteller} ${batterie.modell}`,
      detail: `${batterieKwh} kWh nutzbar · ${eur(batterie.preisEur)} netto`,
    });
  }
  rows.push({
    pos: pos++,
    menge: `${stringKonfiguration.mppTracker} Strings`,
    bezeichnung: "String-Konfiguration (DC)",
    detail: `${stringKonfiguration.stringLaenge} Module/Reihe · Verteilung ${stringKonfiguration.modulePro.join(" / ")} · ≈ ${(stringKonfiguration.stringLaenge * modulVoc).toFixed(0)} V Voc/String`,
  });

  const today = new Date().toLocaleDateString("de-AT");

  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)",
        zIndex: 1000, overflow: "auto", padding: 24,
      }}
      onClick={onClose}
    >
      <style>{PRINT_CSS}</style>
      <div
        id="bom-print-overlay"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 800, margin: "0 auto", background: "#fff", borderRadius: 8,
          padding: 32, boxShadow: "0 20px 60px rgba(0,0,0,0.3)", color: "#111827",
        }}
      >
        {/* Kopf */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", borderBottom: "2px solid #1e3a5f", paddingBottom: 12, marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#1e3a5f" }}>Anlagen-Stückliste & Montageplan</div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
              {projectName ? `Projekt: ${projectName}` : "PV-Auslegung"}
              {customerName ? ` · ${customerName}` : ""} · Stand {today}
            </div>
          </div>
          <div style={{ textAlign: "right", fontSize: 11, color: "#6b7280" }}>
            ELMETIC EnergyOS<br />Interner Entwurf
          </div>
        </div>

        {/* Sicherheits-Badges */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
          {["ENTWURF", "Kein Angebot", "Keine Freigabe", "Menschliche Prüfung erforderlich"].map((b) => (
            <span key={b} style={{ fontSize: 10, fontWeight: 700, background: "#f3f4f6", color: "#374151", padding: "2px 8px", borderRadius: 999 }}>{b}</span>
          ))}
        </div>

        {/* Montageplan-Eckdaten */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 18 }}>
          {[
            { l: "Anlagengröße", v: `${kwp.toFixed(2)} kWp` },
            { l: "Module", v: `${moduleCount} Stk` },
            { l: "Wechselrichter", v: `${wechselrichter.leistungKw} kW` },
            { l: "Speicher", v: batterie ? `${batterieKwh} kWh` : "—" },
          ].map((kpi) => (
            <div key={kpi.l} style={{ background: "#f8fafc", borderRadius: 8, padding: "10px 12px", border: "1px solid #e5e7eb" }}>
              <div style={{ fontSize: 10, color: "#6b7280" }}>{kpi.l}</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#1e3a5f" }}>{kpi.v}</div>
            </div>
          ))}
        </div>

        {/* Bauteilliste */}
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, color: "#1e3a5f" }}>Bauteilliste (Hauptkomponenten)</div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, marginBottom: 18 }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #e5e7eb", textAlign: "left" }}>
              <th style={{ padding: "6px 8px", width: 40 }}>Pos</th>
              <th style={{ padding: "6px 8px", width: 80 }}>Menge</th>
              <th style={{ padding: "6px 8px" }}>Bezeichnung</th>
              <th style={{ padding: "6px 8px" }}>Details</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.pos} style={{ borderBottom: "1px solid #f3f4f6" }}>
                <td style={{ padding: "6px 8px", color: "#6b7280" }}>{r.pos}</td>
                <td style={{ padding: "6px 8px", fontWeight: 600 }}>{r.menge}</td>
                <td style={{ padding: "6px 8px", fontWeight: 600 }}>{r.bezeichnung}</td>
                <td style={{ padding: "6px 8px", color: "#374151" }}>{r.detail}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Komponentenkosten */}
        <div style={{ background: "#1e3a5f", color: "#fff", borderRadius: 8, padding: "12px 16px", display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 11, color: "#93c5fd" }}>Komponentenkosten netto (ohne Module + Montage)</div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>{eur(gesamtpreisNetto)}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 11, color: "#93c5fd" }}>Brutto (inkl. 20% MwSt.)</div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>{eur(gesamtpreisBrutto)}</div>
          </div>
        </div>

        {/* Disclaimer */}
        <div style={{ fontSize: 10, color: "#6b7280", lineHeight: 1.5, borderTop: "1px solid #e5e7eb", paddingTop: 10 }}>
          ENTWURF — Diese Stückliste ist ein unverbindlicher Auslegungsentwurf, kein Angebot. Module-Stückzahl
          gerundet aus kWp/Modulleistung. String-Auslegung ist eine Näherung; verbindliche Netzzulassung
          (Type/Firmware) über wechselrichterliste.at. Keine statische/elektrische/behördliche Freigabe.
          Endprüfung durch qualifizierte Fachkraft zwingend erforderlich. Keine Freigabe durch KI.
        </div>

        {/* Aktionen (nicht druckbar) */}
        <div className="bom-noprint" style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
          <button
            onClick={onClose}
            style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid #d1d5db", background: "#fff", fontSize: 13, cursor: "pointer" }}
          >
            Schließen
          </button>
          <button
            onClick={() => window.print()}
            style={{ padding: "8px 16px", borderRadius: 6, border: "none", background: "#2563eb", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
          >
            🖨 Drucken / PDF
          </button>
        </div>
      </div>
    </div>
  );
}
